import { ChatMessage } from '@app/types/chat';
import { Author, ChatType } from '@utils/enums.ts';

export interface DocumentChatChunk {
  chat_id?: number | null;
  chat_type?: string;
  message_id?: number | string;
  text?: string;
  status?: string;
  document_id?: number | null;
}

/**
 * Fold one streamed chunk of a document chat reply into the open chat's
 * messages. Each chunk carries the whole reply so far, so the bubble is
 * replaced, not appended to.
 *
 * Replies are delivered to every tab the asker has open (ZLAYT-1373), so a
 * chunk for a chat other than the one on screen is ignored. When the open
 * chat is not known yet the chunk is accepted, as before.
 */
export function applyDocumentChatChunk(
  prevMessages: ChatMessage[],
  chunk: DocumentChatChunk,
  activeChatId: number | null | undefined
): ChatMessage[] {
  const { chat_id: chatId, message_id: chunkId, text = '', status } = chunk;

  if (!chunkId) {
    return prevMessages;
  }
  if (activeChatId && chatId && chatId !== activeChatId) {
    return prevMessages;
  }

  const existingIndex = prevMessages.findIndex(
    (msg) => msg.streaming_id === chunkId
  );

  if (existingIndex !== -1) {
    const updated = [...prevMessages];
    updated[existingIndex] = {
      ...updated[existingIndex],
      text,
      is_streaming: status !== 'completed',
      status: status || updated[existingIndex].status,
    };
    return updated;
  }

  const placeholder: ChatMessage = {
    id: -Math.abs(Number(chunkId)),
    streaming_id: chunkId,
    text,
    author: Author.Chatbot,
    document_id: chunk.document_id ?? 0,
    chat_type: ChatType.documentVersion,
    status: status || 'streaming',
    is_streaming: status !== 'completed',
  };

  return [...prevMessages, placeholder];
}

/**
 * Fold a saved document chat reply (a `message_response` event) into the open
 * chat's messages, replacing its streaming placeholder if there is one.
 *
 * Like the chunks, a reply pushed to the asker reaches every tab they have
 * open, so a reply for a chat other than the one on screen is ignored. When
 * the open chat is not known yet the reply is accepted, as before.
 */
export function applyDocumentChatReply(
  prevMessages: ChatMessage[],
  reply: ChatMessage,
  activeChatId: number | null | undefined
): ChatMessage[] {
  const chatId = reply.chat_id;
  if (activeChatId && chatId && chatId !== activeChatId) {
    return prevMessages;
  }

  const withoutStreaming = prevMessages.filter(
    (msg) => !msg.is_streaming || msg.streaming_id !== reply.id
  );
  if (withoutStreaming.find((msg) => msg.id === reply.id)) {
    return withoutStreaming;
  }
  return [...withoutStreaming, reply];
}
